import { useState } from "react";
import type { PendingInvitation, Role, TeamMember } from "@cura-studio/shared";
import { ApiError } from "@/api/client";
import {
  useInviteTeamMember,
  useResendInvitation,
  useRevokeInvitation,
  useTeam,
  useUpdateTeamMember,
} from "@/api/teamMutations";
import { useMe } from "@/api/useMe";
import { Dialog } from "@/components/Dialog";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/Button";

/**
 * Team — tenant-scoped user management for CLIENT_ADMINs (and STUDIO_ADMINs
 * scoped into a tenant). Mirrors the admin Clients › Users tab visually but
 * adds in-row role + status mutations, plus a Pending invitations section.
 *
 * Wire spec: docs/TEAM_API.md (Agent A owns the backend). Hooks invalidate
 * the `["team"]` query key on every mutation so the table re-fetches.
 */
export function TeamPage() {
  const { data: me } = useMe();
  const { data, isLoading, isError, error, refetch } = useTeam();
  const [inviteOpen, setInviteOpen] = useState(false);

  const isStudioAdmin = me?.user?.role === "STUDIO_ADMIN";
  const callerId = me?.user?.id ?? null;
  const tenantName = me?.tenant?.name ?? "your team";

  const members = data?.members ?? [];
  const pending = data?.pending ?? [];

  return (
    <div className="mx-auto max-w-[1180px] px-8 py-10">
      <header className="mb-8 flex items-end justify-between gap-5">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-mute">
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: "var(--studio)" }}
            />
            Team
          </div>
          <h1 className="m-0 mb-2 text-[28px] font-semibold leading-tight text-fg-1">
            Manage your tenant's users
          </h1>
          <p className="m-0 max-w-2xl text-[15px] leading-relaxed text-fg-3">
            Invite new users, change roles, and suspend access. Invited users get
            a magic-link email — they activate by clicking it.
          </p>
        </div>
        <Button
          onClick={() => setInviteOpen(true)}
          className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
        >
          + Invite user
        </Button>
      </header>

      <InviteTeamMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        tenantName={tenantName}
        isStudioAdmin={isStudioAdmin}
      />

      {isLoading ? (
        <div className="rounded-lg border border-[var(--border-1)] bg-white p-8 text-fg-3">
          Loading team…
        </div>
      ) : isError ? (
        <div className="flex items-center justify-between rounded-lg border border-[#E2A0A0] bg-[#FBE9E9] p-4 text-[13px] text-[#8C2020]">
          <span>
            Could not load team
            {error instanceof ApiError ? ` (HTTP ${error.status})` : ""}.
          </span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md border border-[#E2A0A0] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#8C2020] hover:bg-[#FBE9E9]"
          >
            Retry
          </button>
        </div>
      ) : members.length === 0 && pending.length === 0 ? (
        <EmptyState onInvite={() => setInviteOpen(true)} />
      ) : (
        <div className="grid gap-8">
          <MembersTable
            members={members}
            callerId={callerId}
            isStudioAdmin={isStudioAdmin}
          />
          <PendingTable pending={pending} />
        </div>
      )}
    </div>
  );
}

/* ── Empty state ────────────────────────────────────────────────────── */

function EmptyState({ onInvite }: { onInvite: () => void }) {
  return (
    <div className="rounded-lg border border-[var(--border-1)] bg-white p-12 text-center">
      <div
        className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full"
        style={{ background: "var(--studio-wash)" }}
        aria-hidden
      >
        <span
          className="block h-2 w-2 rounded-full"
          style={{ background: "var(--studio)" }}
        />
      </div>
      <h2 className="m-0 mb-1.5 text-[16px] font-semibold text-fg-1">
        No one else on your team yet
      </h2>
      <p className="mx-auto m-0 mb-5 max-w-md text-[13px] leading-relaxed text-fg-3">
        Invite someone to get started — they'll receive a magic-link email that
        activates their account on first sign-in.
      </p>
      <Button
        onClick={onInvite}
        className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
      >
        + Invite your first user
      </Button>
    </div>
  );
}

/* ── Members table ──────────────────────────────────────────────────── */

function MembersTable({
  members,
  callerId,
  isStudioAdmin,
}: {
  members: TeamMember[];
  callerId: string | null;
  isStudioAdmin: boolean;
}) {
  return (
    <section>
      <h2 className="m-0 mb-3 text-[14px] font-semibold text-fg-1">
        Members <span className="text-fg-3">({members.length})</span>
      </h2>
      <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--border-1)] bg-bg-2 text-left">
              {["Name", "Email", "Role", "Status", "Last seen", ""].map((h, i) => (
                <th
                  key={`${h}-${i}`}
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-fg-3"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <MemberRow
                key={m.id}
                member={m}
                isSelf={m.id === callerId}
                isStudioAdmin={isStudioAdmin}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MemberRow({
  member,
  isSelf,
  isStudioAdmin,
}: {
  member: TeamMember;
  isSelf: boolean;
  isStudioAdmin: boolean;
}) {
  const updateMember = useUpdateTeamMember();
  const [rowError, setRowError] = useState<string | null>(null);

  const roles = roleOptions(isStudioAdmin);
  const canMutate = !isSelf;

  async function onRoleChange(next: Role) {
    if (next === member.role) return;
    setRowError(null);
    try {
      await updateMember.mutateAsync({
        userId: member.id,
        patch: { role: next },
      });
    } catch (err) {
      setRowError(extractError(err, "Could not update role."));
    }
  }

  async function onToggleSuspend() {
    setRowError(null);
    const next = member.status === "SUSPENDED" ? "ACTIVE" : "SUSPENDED";
    try {
      await updateMember.mutateAsync({
        userId: member.id,
        patch: { status: next },
      });
    } catch (err) {
      setRowError(extractError(err, "Could not change status."));
    }
  }

  const lastSeen = member.lastSeenAt
    ? new Date(member.lastSeenAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

  return (
    <>
      <tr className="border-b border-[var(--border-1)] last:border-0">
        <td className="px-3 py-2.5 text-[13px] font-semibold text-fg-1">
          {member.name}
          {isSelf && (
            <span className="ml-2 rounded bg-bg-2 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fg-3">
              You
            </span>
          )}
        </td>
        <td className="px-3 py-2.5 font-mono text-[12px] text-fg-3">
          {member.email}
        </td>
        <td className="px-3 py-2.5">
          <select
            value={member.role}
            onChange={(e) => onRoleChange(e.target.value as Role)}
            disabled={!canMutate || updateMember.isPending}
            className="rounded border border-[var(--border-2)] bg-white px-2 py-1 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)] disabled:cursor-not-allowed disabled:opacity-60"
            aria-label={`Role for ${member.name}`}
          >
            {roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </td>
        <td className="px-3 py-2.5">
          <StatusPill status={member.status.toLowerCase()} />
        </td>
        <td className="px-3 py-2.5 text-[13px] text-fg-3">{lastSeen}</td>
        <td className="px-3 py-2.5 text-right">
          {/* No suspend action for users who haven't activated yet — they'd
              just sit in INVITED, which isn't a server-allowed transition. */}
          {member.status !== "INVITED" && (
            <Button
              variant={member.status === "SUSPENDED" ? "secondary" : "danger"}
              size="sm"
              onClick={onToggleSuspend}
              disabled={!canMutate || updateMember.isPending}
              title={
                isSelf
                  ? "You can't suspend your own account."
                  : member.status === "SUSPENDED"
                    ? "Restore access"
                    : "Block sign-in for this user"
              }
            >
              {member.status === "SUSPENDED" ? "Reactivate" : "Suspend"}
            </Button>
          )}
        </td>
      </tr>
      {rowError && (
        <tr className="border-b border-[var(--border-1)] last:border-0">
          <td
            colSpan={6}
            className="bg-[#FBE9E9] px-3 py-2 text-[12px] text-[#8C2020]"
          >
            {rowError}
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Pending invitations ─────────────────────────────────────────────── */

function PendingTable({ pending }: { pending: PendingInvitation[] }) {
  if (pending.length === 0) {
    // Quiet on purpose — empty pending list isn't worth a whole panel.
    return null;
  }

  return (
    <section>
      <h2 className="m-0 mb-3 text-[14px] font-semibold text-fg-1">
        Pending invitations <span className="text-fg-3">({pending.length})</span>
      </h2>
      <div className="overflow-hidden rounded-lg border border-[var(--border-1)] bg-white">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--border-1)] bg-bg-2 text-left">
              {["Name", "Email", "Role", "Invited by", "Expires", ""].map((h, i) => (
                <th
                  key={`${h}-${i}`}
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-fg-3"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pending.map((p) => (
              <PendingRow key={p.id} invitation={p} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PendingRow({ invitation }: { invitation: PendingInvitation }) {
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  const [rowError, setRowError] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const expires = new Date(invitation.expiresAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const expired = new Date(invitation.expiresAt).getTime() < Date.now();

  async function onResend() {
    setRowError(null);
    try {
      const res = await resend.mutateAsync(invitation.id);
      setMagicLink(res.magicLink);
    } catch (err) {
      setRowError(extractError(err, "Could not resend invitation."));
    }
  }

  async function onRevoke() {
    setRowError(null);
    try {
      await revoke.mutateAsync(invitation.id);
    } catch (err) {
      setRowError(extractError(err, "Could not revoke invitation."));
    }
  }

  async function onCopy() {
    if (!magicLink) return;
    try {
      await navigator.clipboard?.writeText(magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail behind some browser settings; silent is fine,
      // the link is still visible in the inline panel.
    }
  }

  const busy = resend.isPending || revoke.isPending;

  return (
    <>
      <tr className="border-b border-[var(--border-1)] last:border-0">
        <td className="px-3 py-2.5 text-[13px] font-semibold text-fg-1">
          {invitation.name}
        </td>
        <td className="px-3 py-2.5 font-mono text-[12px] text-fg-3">
          {invitation.email}
        </td>
        <td className="px-3 py-2.5 text-[13px] text-fg-2">
          {roleLabel(invitation.role)}
        </td>
        <td className="px-3 py-2.5 text-[13px] text-fg-3">
          {invitation.invitedByName}
        </td>
        <td className="px-3 py-2.5 text-[13px]">
          <span className={expired ? "text-[#8C2020]" : "text-fg-3"}>
            {expires}
            {expired && (
              <span className="ml-1.5 font-semibold uppercase">expired</span>
            )}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right">
          <div className="inline-flex gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              onClick={onResend}
              disabled={busy}
            >
              {resend.isPending ? "Resending…" : "Resend"}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={onRevoke}
              disabled={busy}
            >
              {revoke.isPending ? "Revoking…" : "Revoke"}
            </Button>
          </div>
        </td>
      </tr>
      {(magicLink || rowError) && (
        <tr className="border-b border-[var(--border-1)] last:border-0">
          <td colSpan={6} className="bg-bg-1 px-3 py-2.5">
            {rowError && (
              <div className="mb-2 text-[12px] text-[#8C2020]">{rowError}</div>
            )}
            {magicLink && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-mute">
                  Magic link
                </span>
                <pre className="m-0 flex-1 overflow-x-auto rounded border border-[var(--border-1)] bg-white px-2 py-1 font-mono text-[11px] text-fg-1">
                  {magicLink}
                </pre>
                <Button variant="secondary" size="sm" onClick={onCopy}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Invite dialog ──────────────────────────────────────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function InviteTeamMemberDialog({
  open,
  onOpenChange,
  tenantName,
  isStudioAdmin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantName: string;
  isStudioAdmin: boolean;
}) {
  const [form, setForm] = useState<{ email: string; name: string; role: Role }>({
    email: "",
    name: "",
    role: "PRACTICE_MANAGER",
  });
  const [serverError, setServerError] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const mutation = useInviteTeamMember();

  const roles = roleOptions(isStudioAdmin);
  const errs: Partial<Record<"email" | "name", string>> = {};
  if (!EMAIL_RE.test(form.email)) errs.email = "Valid email required.";
  if (form.name.trim().length < 2) errs.name = "Name required.";
  const canSubmit = Object.keys(errs).length === 0 && !mutation.isPending;

  function reset() {
    setForm({ email: "", name: "", role: "PRACTICE_MANAGER" });
    setServerError(null);
    setMagicLink(null);
    setCopied(false);
    mutation.reset();
  }

  async function onSubmit() {
    setServerError(null);
    try {
      const res = await mutation.mutateAsync({
        ...form,
        email: form.email.toLowerCase(),
      });
      setMagicLink(res.magicLink);
    } catch (err) {
      setServerError(extractError(err, "Could not invite user."));
    }
  }

  async function onCopy() {
    if (!magicLink) return;
    try {
      await navigator.clipboard?.writeText(magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Same fallback as PendingRow.onCopy — the link stays visible regardless.
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
      title={`Invite a user to ${tenantName}`}
      footer={
        magicLink ? (
          <Button
            className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
            onClick={() => {
              onOpenChange(false);
              reset();
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              loading={mutation.isPending}
              disabled={!canSubmit}
              className="bg-[var(--studio)] hover:bg-[var(--studio-dark)] active:bg-[var(--studio-dark)]"
            >
              {mutation.isPending ? "Inviting…" : "Send invitation"}
            </Button>
          </>
        )
      }
    >
      {magicLink ? (
        <div className="grid gap-3">
          <p className="m-0 text-[13px] text-fg-2">
            Invitation created for <strong>{form.email}</strong>. Email delivery
            wires in Phase 4.5; in the meantime, copy this magic link and send
            it manually.
          </p>
          <pre className="m-0 overflow-x-auto rounded-md bg-bg-1 px-3 py-2 font-mono text-[12px] text-fg-1">
            {magicLink}
          </pre>
          <Button
            variant="secondary"
            size="sm"
            onClick={onCopy}
            className="self-start"
          >
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          <Field label="Full name" error={errs.name}>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Dr Anna Lee"
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            />
          </Field>
          <Field label="Email" error={errs.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="anna.lee@northshore.nhs.uk"
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            />
          </Field>
          <Field
            label="Role"
            hint={
              isStudioAdmin
                ? "Studio admins manage every tenant. Client admins manage their own. Practice managers order, Approvers sign off, Designers edit drafts, Viewers are read-only."
                : "Client admins manage their tenant's users. Practice managers can order, Approvers sign off proofs, Designers edit drafts, Viewers are read-only."
            }
          >
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
              className="w-full rounded border border-[var(--border-2)] bg-white px-3 py-2 text-[13px] outline-none focus:border-fg-1 focus:ring-2 focus:ring-[var(--border-focus)]"
            >
              {roles.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </Field>
          {serverError && (
            <div className="rounded-md border border-[#E2A0A0] bg-[#FBE9E9] px-3 py-2 text-[13px] text-[#8C2020]">
              {serverError}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

/* ── Shared helpers ─────────────────────────────────────────────────── */

const BASE_ROLES: { value: Role; label: string }[] = [
  { value: "CLIENT_ADMIN", label: "Client admin" },
  { value: "PRACTICE_MANAGER", label: "Practice manager" },
  { value: "APPROVER", label: "Approver" },
  { value: "DESIGNER", label: "Designer" },
  { value: "VIEWER", label: "Viewer" },
];

function roleOptions(isStudioAdmin: boolean): { value: Role; label: string }[] {
  // Studio admins can assign every role including STUDIO_ADMIN; client admins
  // can never assign STUDIO_ADMIN — the server enforces this regardless.
  return isStudioAdmin
    ? [{ value: "STUDIO_ADMIN", label: "Studio admin" }, ...BASE_ROLES]
    : BASE_ROLES;
}

function roleLabel(role: Role): string {
  return (
    BASE_ROLES.find((r) => r.value === role)?.label ??
    (role === "STUDIO_ADMIN" ? "Studio admin" : role)
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-semibold text-fg-2">
        {label}
      </span>
      {children}
      {hint && !error && (
        <span className="mt-1 block text-[11px] text-fg-3">{hint}</span>
      )}
      {error && (
        <span className="mt-1 block text-[11px] text-[#8C2020]">{error}</span>
      )}
    </label>
  );
}

function extractError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? `${fallback} (HTTP ${err.status})`;
  }
  return fallback;
}
